/**
 * Resolve the run + conversation to open when a user views an agent instance.
 * Uses the latest run for the instance (or creates one), then the latest
 * conversation on that run (or creates one with the default Agent - datetime title).
 */

import { createRun, getInstance, getRun } from '@/services/aiStudioAgentService';
import { getInstanceSetupStatus } from '@/services/aiStudioInstanceSetupService';
import { createConversation, listConversations } from '@/services/aiStudioService';
import { listMyRuns } from '@/services/aiStudioMonitorService';
import { isInstanceRunnable } from './instanceRunEligibility';
import type { StudioBindingStatus } from '@/types/aiStudioAgent';

export class InstanceSetupRequiredError extends Error {
  readonly code = 'INSTANCE_SETUP_REQUIRED';

  constructor(public readonly instanceId: string) {
    super('Instance setup must be completed before running.');
    this.name = 'InstanceSetupRequiredError';
  }
}

export class InstanceNotRunnableError extends Error {
  readonly code = 'INSTANCE_NOT_RUNNABLE';

  constructor(
    public readonly instanceId: string,
    public readonly reason: 'inactive' | 'archived' | 'setup_incomplete'
  ) {
    super(
      reason === 'archived'
        ? 'This instance is archived. Set status to Active from My instances to run again.'
        : reason === 'inactive'
          ? 'This instance is inactive. Set status to Active from My instances to run.'
          : 'Instance setup must be completed before running.'
    );
    this.name = 'InstanceNotRunnableError';
  }
}

export interface InstanceEntryTarget {
  runId: string;
  conversationId: string;
}

function pickLatestConversationId(
  conversations: Awaited<ReturnType<typeof listConversations>>
): string | null {
  if (conversations.length === 0) return null;
  const sorted = [...conversations].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  return sorted[0]?.id ?? null;
}

function assertInstanceCanRun(
  instanceId: string,
  status: StudioBindingStatus,
  setupConfigured: boolean | undefined
): void {
  if (status === 'inactive') {
    throw new InstanceNotRunnableError(instanceId, 'inactive');
  }
  if (status === 'archived') {
    throw new InstanceNotRunnableError(instanceId, 'archived');
  }
  if (status !== 'active' || setupConfigured === false) {
    throw new InstanceNotRunnableError(instanceId, 'setup_incomplete');
  }
}

/** Open instance → latest run conversation, or create run + conversation when none exist. */
export async function resolveInstanceEntry(instanceId: string): Promise<InstanceEntryTarget> {
  const [instance, setupStatus] = await Promise.all([
    getInstance(instanceId),
    getInstanceSetupStatus(instanceId).catch(() => null),
  ]);

  const setupConfigured =
    setupStatus?.requiresSetup === false
      ? true
      : (setupStatus?.configured ?? instance.setupConfigured ?? false);

  assertInstanceCanRun(instanceId, instance.status, setupConfigured);

  if (setupStatus?.requiresSetup && setupStatus.status !== 'ready') {
    throw new InstanceSetupRequiredError(instanceId);
  }

  if (!isInstanceRunnable({ status: instance.status, setupConfigured })) {
    throw new InstanceNotRunnableError(instanceId, 'setup_incomplete');
  }

  let runId: string;
  const recentRuns = await listMyRuns({ instanceId, limit: 1 });
  const latestRunRow = recentRuns[0];
  if (latestRunRow?.['id']) {
    runId = String(latestRunRow['id']);
  } else {
    const run = await createRun({
      instanceId,
      runName: instance.instanceName,
    });
    runId = run.id;
  }

  const conversations = await listConversations({ contextType: 'AGENT_RUN', runId });
  const existingConversationId = pickLatestConversationId(conversations);
  if (existingConversationId) {
    return { runId, conversationId: existingConversationId };
  }

  const run = await getRun(runId);
  const created = await createConversation({
    contextType: 'AGENT_RUN',
    runId,
    bindingId: run.bindingId ?? instance.id,
    contextRef: { label: run.agentName ?? instance.instanceName },
  });

  return { runId, conversationId: created.id };
}
